import { lifetimeConfigParams, spatialTypeOfMovement } from '@hub/bearing-hub/common/constants';
import {
  type LifetimeConfig,
  type LifetimeParameters,
  type LinearAndPivotingLifetimeConfig,
  type LinearAndRotationLifetimeConfig,
  type LinearLifetimeConfig,
  type PivotingLifetimeConfig,
  type RotationLifetimeConfig,
  temporalTypeOfMovement,
} from '@hub/bearing-hub/common/types';
import { ForceCode, LengthCode } from '@icommerce/units';
import {
  IbhSpatialTypeOfMovements,
  type IbhTechnicalEnvironment,
  type IbhTemporalTypeOfMovement,
} from '@igus/iglidur-bearing-hub-api';
import { BearingHubHelper } from '@hub/libs/bearing-hub.helper';
import { IbhUnitSystem } from '@igus/iglidur-bearing-hub-api/src/data-contracts';

export type ArticleLifetimeParams = {
  acceptableWear?: number;
  activityMinutesPerHour?: number;
  environment?: IbhTechnicalEnvironment;
  force?: number;
  linearFrequency?: number;
  linearStrokeLength?: number;
  oppositeMaterialId?: string;
  pivotingAngle?: number;
  pivotingFrequency?: number;
  rotationRevolutions?: number;
  temperature?: number;
  offTime?: number;
  onTime?: number;
  typeOfMovement?: IbhSpatialTypeOfMovements;
  temporalTypeOfMovement?: IbhTemporalTypeOfMovement;
  temporalLinearPart?: number;
};

const removeUndefinedKeys = <T extends object>(obj: T) => {
  const keys = Object.keys(obj);
  const filteredKeys = keys.filter(key => obj[key as keyof T] !== undefined);
  return filteredKeys.reduce((acc, key) => {
    acc[key as keyof T] = obj[key as keyof T];
    return acc;
  }, {} as T);
};

const createBaseLifetimeConfigPayload = (lifetimeParameters: LifetimeParameters, flatStructure: boolean) => {
  const config = {
    [lifetimeConfigParams.acceptableWear]: lifetimeParameters.acceptableWear,
    [lifetimeConfigParams.activityMinutesPerHour]: lifetimeParameters.activityMinutesPerHour,
    [lifetimeConfigParams.environment]: lifetimeParameters.environment,
    [lifetimeConfigParams.oppositeMaterialId]: lifetimeParameters.oppositeMaterialId?.toString(),
    [lifetimeConfigParams.temperature]: lifetimeParameters.temperature,
    [lifetimeConfigParams.typeOfMovement]: lifetimeParameters.typeOfMovement,
  };
  if (flatStructure) {
    config[lifetimeConfigParams.onTime] = lifetimeParameters.onTime || 0;
    config[lifetimeConfigParams.offTime] = lifetimeParameters.offTime || 0;
    config[lifetimeConfigParams.force] = lifetimeParameters.force;
  } else {
    config[lifetimeConfigParams.intermittentParams] = {
      [lifetimeConfigParams.onTime]: lifetimeParameters.onTime || 0,
      [lifetimeConfigParams.offTime]: lifetimeParameters.offTime || 0,
    };
    config[lifetimeConfigParams.load] = { force: lifetimeParameters.force };
  }

  return config;
};

const createLinearLifetimeConfigPayload = (lifetimeParameters: LifetimeParameters, flatStructure: boolean) =>
  ({
    [spatialTypeOfMovement.linear]: removeUndefinedKeys({
      ...createBaseLifetimeConfigPayload(lifetimeParameters, flatStructure),
      [lifetimeConfigParams.linearFrequency]: lifetimeParameters.linearFrequency,
      [lifetimeConfigParams.linearStrokeLength]: lifetimeParameters.linearStrokeLength,
    }),
  } as LinearLifetimeConfig);

const createPivotingLifetimeConfigPayload = (lifetimeParameters: LifetimeParameters, flatStructure: boolean) =>
  ({
    [spatialTypeOfMovement.pivoting]: removeUndefinedKeys({
      ...createBaseLifetimeConfigPayload(lifetimeParameters, flatStructure),
      [lifetimeConfigParams.pivotingAngle]: lifetimeParameters.pivotingAngle,
      [lifetimeConfigParams.pivotingFrequency]: lifetimeParameters.pivotingFrequency,
    }),
  } as PivotingLifetimeConfig);

const createRotationalLifetimeConfigPayload = (lifetimeParameters: LifetimeParameters, flatStructure: boolean) =>
  ({
    [spatialTypeOfMovement.rotation]: removeUndefinedKeys({
      ...createBaseLifetimeConfigPayload(lifetimeParameters, flatStructure),
      [lifetimeConfigParams.rotationRevolutions]: lifetimeParameters.rotationRevolutions,
    }),
  } as RotationLifetimeConfig);

const createLinearAndPivotingLifetimeConfigPayload = (lifetimeParameters: LifetimeParameters, flatStructure: boolean) =>
  ({
    [spatialTypeOfMovement.linearAndPivoting]: removeUndefinedKeys({
      ...createBaseLifetimeConfigPayload(lifetimeParameters, flatStructure),
      [lifetimeConfigParams.pivotingAngle]: lifetimeParameters.pivotingAngle,
      [lifetimeConfigParams.pivotingFrequency]: lifetimeParameters.pivotingFrequency,
      [lifetimeConfigParams.linearFrequency]: lifetimeParameters.linearFrequency,
      [lifetimeConfigParams.linearStrokeLength]: lifetimeParameters.linearStrokeLength,
      [lifetimeConfigParams.temporalLinearPart]: lifetimeParameters.temporalLinearPart,
      [lifetimeConfigParams.temporalTypeOfMovement]: lifetimeParameters.temporalTypeOfMovement,
    }),
  } as LinearAndPivotingLifetimeConfig);

const createLinearAndRotatingLifetimeConfigPayload = (lifetimeParameters: LifetimeParameters, flatStructure: boolean) =>
  ({
    [spatialTypeOfMovement.linearAndRotation]: removeUndefinedKeys({
      ...createBaseLifetimeConfigPayload(lifetimeParameters, flatStructure),
      [lifetimeConfigParams.linearFrequency]: lifetimeParameters.linearFrequency,
      [lifetimeConfigParams.linearStrokeLength]: lifetimeParameters.linearStrokeLength,
      [lifetimeConfigParams.rotationRevolutions]: lifetimeParameters.rotationRevolutions,
      [lifetimeConfigParams.temporalLinearPart]: lifetimeParameters.temporalLinearPart,
      [lifetimeConfigParams.temporalTypeOfMovement]: lifetimeParameters.temporalTypeOfMovement,
    }),
  } as LinearAndRotationLifetimeConfig);

export const createLifetimeConfigPayload = (
  lifetimeParameters?: LifetimeParameters,
  unitSystem = IbhUnitSystem.Metric,
  flatStructure = false
): LifetimeConfig[keyof LifetimeConfig] | undefined | null => {
  const { typeOfMovement, force, acceptableWear, linearStrokeLength } = lifetimeParameters;
  const newLifetimeParameters = {
    ...lifetimeParameters,
    force: BearingHubHelper.convertForceUnit(
      { value: force, unit: unitSystem === IbhUnitSystem.Metric ? ForceCode.NEWTON : ForceCode.POUND_FORCE },
      ForceCode.NEWTON
    ),
    acceptableWear: BearingHubHelper.convertLengthUnit(
      { value: acceptableWear, unit: unitSystem === IbhUnitSystem.Metric ? LengthCode.MILLIMETER : LengthCode.INCH },
      LengthCode.MILLIMETER
    ),
    [lifetimeConfigParams.linearStrokeLength]: BearingHubHelper.convertLengthUnit(
      {
        value: linearStrokeLength,
        unit: unitSystem === IbhUnitSystem.Metric ? LengthCode.MILLIMETER : LengthCode.INCH,
      },
      LengthCode.MILLIMETER
    ),
  };

  if (!typeOfMovement) {
    return undefined;
  }
  if (typeOfMovement === IbhSpatialTypeOfMovements.Linear) {
    return createLinearLifetimeConfigPayload(newLifetimeParameters, flatStructure);
  }

  if (typeOfMovement === IbhSpatialTypeOfMovements.Pivoting) {
    return createPivotingLifetimeConfigPayload(newLifetimeParameters, flatStructure);
  }

  if (typeOfMovement === IbhSpatialTypeOfMovements.Rotation) {
    return createRotationalLifetimeConfigPayload(newLifetimeParameters, flatStructure);
  }

  if (typeOfMovement === IbhSpatialTypeOfMovements.LinearAndPivoting) {
    return createLinearAndPivotingLifetimeConfigPayload(newLifetimeParameters, flatStructure);
  }

  if (typeOfMovement === IbhSpatialTypeOfMovements.LinearAndRotation) {
    return createLinearAndRotatingLifetimeConfigPayload(newLifetimeParameters, flatStructure);
  }
  return createLinearLifetimeConfigPayload(newLifetimeParameters, flatStructure);
};

export const createArticleLifeTimePayload = (
  lifetimeParameters: LifetimeParameters,
  unitSystem = IbhUnitSystem.Metric
) => {
  if (!Object.keys(lifetimeParameters).length) {
    return {};
  }
  const {
    acceptableWear,
    activityMinutesPerHour,
    environment,
    force,
    linearFrequency,
    linearStrokeLength,
    oppositeMaterialId,
    pivotingAngle,
    pivotingFrequency,
    rotationRevolutions,
    temperature,
    onTime,
    offTime,
    typeOfMovement,
  } = lifetimeParameters;

  const convertedForce = BearingHubHelper.convertForceUnit(
    { value: force, unit: unitSystem === IbhUnitSystem.Metric ? ForceCode.NEWTON : ForceCode.POUND_FORCE },
    ForceCode.NEWTON
  );
  const convertedAcceptableWear = BearingHubHelper.convertLengthUnit(
    { value: acceptableWear, unit: unitSystem === IbhUnitSystem.Metric ? LengthCode.MILLIMETER : LengthCode.INCH },
    LengthCode.MILLIMETER
  );
  const convertedLinearStrokeLength = BearingHubHelper.convertLengthUnit(
    { value: linearStrokeLength, unit: unitSystem === IbhUnitSystem.Metric ? LengthCode.MILLIMETER : LengthCode.INCH },
    LengthCode.MILLIMETER
  );

  const tempTypeOfMovement = [
    IbhSpatialTypeOfMovements.LinearAndPivoting,
    IbhSpatialTypeOfMovements.LinearAndRotation,
  ].includes(typeOfMovement)
    ? lifetimeParameters.temporalTypeOfMovement
    : undefined;
  const temporalLinearPart =
    tempTypeOfMovement === temporalTypeOfMovement.sequential ? lifetimeParameters.temporalLinearPart : undefined;

  return removeUndefinedKeys({
    acceptableWear: convertedAcceptableWear,
    activityMinutesPerHour,
    environment,
    force: convertedForce,
    linearFrequency,
    linearStrokeLength: convertedLinearStrokeLength,
    oppositeMaterialId,
    pivotingAngle,
    pivotingFrequency,
    rotationRevolutions,
    temperature,
    typeOfMovement,
    tempTypeOfMovement,
    temporalLinearPart,
    onTime,
    offTime,
  }) as ArticleLifetimeParams;
};
